import { useEffect, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { cn } from "@/lib/utils";
import { uses12HourClock } from "@/lib/timeFormatting";

/**
 * Convert a canonical 24-hour "HH:mm" string to its locale display form:
 * unchanged for 24-hour locales, "h:mm AM/PM" for 12-hour locales
 * ("00:30" → "12:30 AM", "13:05" → "1:05 PM").
 */
export function canonicalToDisplay(canonical: string, use12h: boolean): string {
  const match = /^(\d{2}):(\d{2})$/.exec(canonical);
  if (!match || !use12h) return canonical;
  const h24 = parseInt(match[1], 10);
  const suffix = h24 < 12 ? "AM" : "PM";
  const h12 = h24 % 12 === 0 ? 12 : h24 % 12;
  return `${h12}:${match[2]} ${suffix}`;
}

/**
 * Parse free-typed time text into canonical 24-hour "HH:mm", or null when
 * unparseable. With an AM/PM marker the hour is read on the 12-hour clock
 * ("12:30 am" → "00:30", "1 pm" → "13:00"); without one it is read as
 * 24-hour ("14:30" → "14:30") so both conventions are always accepted.
 * Colonless compact input is split as h(h)mm ("930" → "09:30").
 */
export function parseTimeInput(raw: string): string | null {
  // Trim first so the matcher carries a single, unambiguous `\s*`. A pattern
  // with several `\s*` separated by optional groups can split one whitespace
  // run multiple ways and backtrack polynomially (ReDoS-sensitive); trimming
  // the ends leaves only the one separator between the time and the meridiem.
  const trimmed = raw.trim();
  const match = /^(\d{1,4})(?::(\d{1,2})?)?\s*(?:([ap])\.?m?\.?)?$/i.exec(
    trimmed,
  );
  if (!match) return null;
  let hourStr = match[1];
  let minuteStr = match[2];
  if (!trimmed.includes(":") && hourStr.length >= 3) {
    minuteStr = hourStr.slice(-2);
    hourStr = hourStr.slice(0, -2);
  } else if (hourStr.length > 2) {
    return null;
  }
  let h = parseInt(hourStr, 10);
  const m = Math.min(59, parseInt(minuteStr || "0", 10));
  const meridiem = match[3]?.toLowerCase();
  if (meridiem) {
    h = h % 12; // 12 AM / "0:30 am" → 00, 1–11 unchanged
    if (meridiem === "p") h += 12; // 12 PM → 12, 1–11 PM → 13–23
  } else {
    h = Math.min(23, h);
  }
  return `${String(h).padStart(2, "0")}:${String(m).padStart(2, "0")}`;
}

interface TimeTextInputProps {
  /** Canonical 24-hour "HH:mm" value. */
  readonly value: string;
  /** Called on blur with the normalized canonical "HH:mm" value. */
  readonly onCommit: (canonical: string) => void;
  /** Canonical value committed when the typed text can't be parsed. */
  readonly fallback: string;
  /** Fired on every accepted keystroke (callers tracking dirty state). */
  readonly onDirty?: () => void;
  /** Extra classes; width is owned by the component (12h needs more room). */
  readonly className?: string;
  readonly dataTestId?: string;
}

/**
 * Free-text time-of-day input that adapts to the locale clock: English
 * displays and accepts "h:mm AM/PM" (24-hour typing still works), other
 * locales keep the 24-hour "HH:mm" behaviour. The value exchanged with the
 * parent stays canonical 24-hour "HH:mm" either way, so date assembly
 * (`zonedToApi`, ISO range building) is untouched.
 */
export function TimeTextInput({
  value,
  onCommit,
  fallback,
  onDirty,
  className,
  dataTestId,
}: TimeTextInputProps) {
  const { i18n } = useTranslation();
  const use12h = uses12HourClock(i18n.language);
  const [text, setText] = useState(() => canonicalToDisplay(value, use12h));
  const focusedRef = useRef(false);
  const editedRef = useRef(false);

  // Mirror external value changes (popover re-open, tz late-resolve) into
  // the display, but never clobber the user's in-progress keystrokes. A ref
  // (not state) keeps the blur normalization in handleBlur authoritative —
  // an effect keyed on focus would re-derive from a stale prop after blur.
  useEffect(() => {
    if (!focusedRef.current) setText(canonicalToDisplay(value, use12h));
  }, [value, use12h]);

  const handleChange = (next: string) => {
    const allowed = use12h ? /[^\d:apmAPM. ]/g : /[^\d:]/g;
    let v = next.replace(allowed, "");
    // Auto-insert the colon once two hour digits are typed.
    if (/^\d{2}$/.test(v) && text.length < v.length) v += ":";
    const maxLen = use12h ? 9 : 5;
    if (v.length <= maxLen) {
      editedRef.current = true;
      onDirty?.();
      setText(v);
    }
  };

  const handleBlur = () => {
    focusedRef.current = false;
    // Untouched blur: resync from the current canonical value instead of
    // committing — the displayed text may predate an external value update
    // that arrived while focused, and committing it would push stale time.
    if (!editedRef.current) {
      setText(canonicalToDisplay(value, use12h));
      return;
    }
    const canonical = parseTimeInput(text) ?? fallback;
    setText(canonicalToDisplay(canonical, use12h));
    onCommit(canonical);
  };

  return (
    <input
      type="text"
      inputMode={use12h ? "text" : "numeric"}
      placeholder={use12h ? "h:mm AM/PM" : "HH:mm"}
      data-testid={dataTestId}
      value={text}
      onChange={(e) => handleChange(e.target.value)}
      onFocus={() => {
        focusedRef.current = true;
        editedRef.current = false;
      }}
      onBlur={handleBlur}
      className={cn(
        use12h ? "w-[92px]" : "w-[70px]",
        "h-8 rounded-md border bg-background px-2 text-center tabular-nums shadow-sm focus-visible:outline-none focus-visible:ring-1 focus-visible:ring-ring",
        className,
      )}
    />
  );
}
